import React, { useState } from 'react';
import { Html } from '@react-three/drei';
import { zoneById } from '../config/zones';
import { useStore } from '../store';
import InfoPin from './InfoPin';

const MZ = zoneById('media-books');
const BASE_X = MZ ? MZ.x : 0;

const LIBERAL = [
  { name: 'MSNBC', desc: 'Progressive cable commentary — opinion-forward analysis of power and politics.' },
  { name: 'The New York Times', desc: 'Deep investigative journalism with a cultural, cosmopolitan lens.' },
  { name: 'CNN', desc: 'Breaking-news cable coverage framed around institutions and crisis.' },
  { name: 'The Guardian', desc: 'Global progressive reporting on justice, climate, and inequality.' },
];
const BALANCED = [
  { name: 'AP', desc: 'The wire standard — fast, sourced, deliberately non-partisan facts.' },
  { name: 'Reuters', desc: 'Global markets and events reported with neutral precision.' },
  { name: 'BBC News', desc: 'Public-service reporting balancing perspectives across the spectrum.' },
  { name: 'The Hill', desc: 'Inside-Washington policy coverage that plays it straight down the middle.' },
];
const CONSERVATIVE = [
  { name: 'Fox News', desc: 'Leading conservative cable — culture, security, and populist critique.' },
  { name: 'The Wall Street Journal', desc: 'Free-market business reporting; a right-leaning editorial page.' },
  { name: 'National Review', desc: 'Intellectual conservatism rooted in tradition and limited government.' },
  { name: 'The Washington Examiner', desc: 'Policy and political coverage from a limited-government viewpoint.' },
];

function Outlet({ x, z, kind, item, seed }) {
  const [hover, setHover] = useState(false);
  const panelsHidden = useStore((s) => s.panelsHidden);
  const color = kind === 'liberal' ? '#1e3a8a' : kind === 'conservative' ? '#7f1d1d' : '#38bdf8';
  const emissive = kind === 'liberal' ? '#2f6bff' : kind === 'conservative' ? '#ff4d3a' : '#38bdf8';
  const pinY = kind === 'balanced' ? 9 : 18;

  return (
    <group
      position={[x, 0, z]}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }}
      onPointerOut={() => setHover(false)}
    >
      {kind === 'balanced' ? (
        <group position={[0, 6, 0]}>
          <mesh>
            <icosahedronGeometry args={[1.6, 0]} />
            <meshStandardMaterial color={color} emissive={emissive} emissiveIntensity={1.4} flatShading />
          </mesh>
          <pointLight color={emissive} distance={16} intensity={1.2} />
        </group>
      ) : kind === 'liberal' ? (
        <mesh position={[0, 8, 0]} castShadow>
          <cylinderGeometry args={[1.3, 2, 16, 6]} />
          <meshStandardMaterial color={color} emissive={emissive} emissiveIntensity={0.35} flatShading roughness={0.6} />
        </mesh>
      ) : (
        <mesh position={[0, 6, 0]} castShadow>
          <boxGeometry args={[4, 12, 4]} />
          <meshStandardMaterial color="#8d8578" emissive={emissive} emissiveIntensity={0.18} flatShading roughness={0.9} />
        </mesh>
      )}

      {/* Gold Info Pin instead of large floating text (respects Hide Panels). */}
      <InfoPin y={pinY} seed={seed} />

      {/* Rich hover tooltip — name + description; hidden when panels are hidden. */}
      {hover && !panelsHidden && (
        <Html position={[0, pinY + 3, 0]} center distanceFactor={40} zIndexRange={[40, 0]}>
          <div className="glass-gold rounded-lg px-3 py-2 w-56 text-center" style={{ pointerEvents: 'none' }}>
            <div className="font-serif text-gold text-sm mb-1">{item.name}</div>
            <div className="font-sans text-white/80 text-[11px] leading-snug">{item.desc}</div>
          </div>
        </Html>
      )}
    </group>
  );
}

// Media & Books zone: real outlets — Liberal Towers (left), Balanced Beacons (river), Conservative Forts (right).
export default function MediaOutlets() {
  const spread = (i, n) => (i - (n - 1) / 2) * 12;
  return (
    <group>
      {LIBERAL.map((it, i) => (
        <Outlet key={`l${i}`} x={BASE_X + spread(i, 4)} z={-52} kind="liberal" item={it} seed={i} />
      ))}
      {BALANCED.map((it, i) => (
        <Outlet key={`b${i}`} x={BASE_X + spread(i, 4) * 0.7} z={i % 2 ? 4 : -4} kind="balanced" item={it} seed={10 + i} />
      ))}
      {CONSERVATIVE.map((it, i) => (
        <Outlet key={`c${i}`} x={BASE_X + spread(i, 4)} z={52} kind="conservative" item={it} seed={20 + i} />
      ))}
    </group>
  );
}
